import { apiClient, type ApiResponse } from './client';

// ─── Types ────────────────────────────────────────────────────────────────────

export interface WorkOrderPayload {
  productionOrderId: string;
  productionOrderNumber: string;
  stageNumber?: number;
  stage_number?: number;
  stageName?: string;
  stage_name?: string;
  workCenterId?: string;
  workCenterName?: string;
  work_center_name?: string;
  machineId?: string | null;
  machineName?: string | null;
  operatorId?: string | null;
  /** The whole crew; operatorId is the lead operator (first of it) */
  operatorIds?: string[];
  operatorName?: string | null;
  operator_name?: string | null;
  shiftId?: string | null;
  shiftName?: string | null;
  plannedQty?: number;
  planned_qty?: number;
  completedQty?: number;
  rejectedQty?: number;
  status?: 'PENDING' | 'IN_PROGRESS' | 'COMPLETED' | 'ON_HOLD' | 'QC_PENDING' | 'QC_FAILED' | 'COMPLETED_QC_PENDING' | string;
  plannedStartDate?: string;
  plannedEndDate?: string;
  actualStartDate?: string | null;
  actualEndDate?: string | null;
  plannedTimeMinutes?: number;
  actualTimeMinutes?: number | null;
  notes?: string | null;
  warehouseId?: string | null;
  qc_passed?: boolean;
  qcPassed?: boolean;
  /** 'REWORK' for a work order raised to rework a stage that failed QC */
  type?: string;
}

export interface WorkOrderResponse {
  id: string;
  wo_number: string;
  production_order_id: string;
  production_order_number: string;
  stage_number: number;
  stage_name: string;
  work_center_id: string;
  work_center_name: string;
  machine_id: string | null;
  machine_name: string | null;
  operator_id: string | null;
  operator_name: string | null;
  shift_id: string | null;
  shift_name: string | null;
  planned_qty: string | number;
  completed_qty: string | number;
  rejected_qty: string | number;
  status: 'PENDING' | 'IN_PROGRESS' | 'COMPLETED' | 'ON_HOLD' | 'QC_PENDING' | 'QC_FAILED' | 'COMPLETED_QC_PENDING' | string;
  planned_start_date: string;
  planned_end_date: string;
  actual_start_date: string | null;
  actual_end_date: string | null;
  planned_time_minutes: number;
  actual_time_minutes: number | null;
  notes: string | null;
  warehouse_id: string | null;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

const BASE = '/api/v1/manufacturing/work-orders';

function toSnake(p: Partial<WorkOrderPayload>) {
  const result: any = {};
  if (p.productionOrderId !== undefined) result.production_order_id = p.productionOrderId;
  if (p.productionOrderNumber !== undefined) result.production_order_number = p.productionOrderNumber;
  if (p.stageNumber !== undefined) result.stage_number = p.stageNumber;
  if (p.stageName !== undefined) result.stage_name = p.stageName;
  if (p.workCenterId !== undefined) result.work_center_id = p.workCenterId;
  if (p.workCenterName !== undefined) result.work_center_name = p.workCenterName;
  if (p.machineId !== undefined) result.machine_id = p.machineId || null;
  if (p.machineName !== undefined) result.machine_name = p.machineName || null;
  if (p.operatorId !== undefined) result.operator_id = p.operatorId || null;
  if (p.operatorIds !== undefined) result.operator_ids = p.operatorIds;
  if (p.operatorName !== undefined) result.operator_name = p.operatorName || null;
  if (p.shiftId !== undefined) result.shift_id = p.shiftId || null;
  if (p.shiftName !== undefined) result.shift_name = p.shiftName || null;
  if (p.plannedQty !== undefined) result.planned_qty = p.plannedQty;
  if (p.completedQty !== undefined) result.completed_qty = p.completedQty;
  if (p.rejectedQty !== undefined) result.rejected_qty = p.rejectedQty;
  if (p.status !== undefined) result.status = p.status;
  if (p.plannedStartDate !== undefined) result.planned_start_date = p.plannedStartDate;
  if (p.plannedEndDate !== undefined) result.planned_end_date = p.plannedEndDate;
  if (p.actualStartDate !== undefined) result.actual_start_date = p.actualStartDate || null;
  if (p.actualEndDate !== undefined) result.actual_end_date = p.actualEndDate || null;
  if (p.plannedTimeMinutes !== undefined) result.planned_time_minutes = p.plannedTimeMinutes;
  if (p.actualTimeMinutes !== undefined) result.actual_time_minutes = p.actualTimeMinutes ?? null;
  if (p.notes !== undefined) result.notes = p.notes || null;
  if (p.warehouseId !== undefined) result.warehouse_id = p.warehouseId || null;
  if (p.qc_passed !== undefined) result.qc_passed = p.qc_passed;
  if (p.qcPassed !== undefined) result.qc_passed = p.qcPassed;
  if (p.type !== undefined) result.type = p.type;
  return result;
}

export async function createWorkOrder(
  payload: WorkOrderPayload
): Promise<ApiResponse<WorkOrderResponse>> {
  const { data } = await apiClient.post<ApiResponse<WorkOrderResponse>>(
    `${BASE}/create`,
    toSnake(payload)
  );
  return data;
}

export async function getAllWorkOrders(
  warehouseId?: string | null
): Promise<ApiResponse<WorkOrderResponse[]>> {
  const params: Record<string, string> = {};
  if (warehouseId) {
    params.warehouse_id = warehouseId;
  }
  try {
    const { data } = await apiClient.get<ApiResponse<WorkOrderResponse[]>>(
      `${BASE}/`,
      { params }
    );
    return data;
  } catch (err: any) {
    // Pages show this message, so a failed list never looks like an empty one
    throw new Error(`Could not load work orders: ${err?.response?.data?.message || err?.message || 'server error'}`);
  }
}

export async function getWorkOrderById(
  id: string
): Promise<ApiResponse<WorkOrderResponse>> {
  const { data } = await apiClient.get<ApiResponse<WorkOrderResponse>>(`${BASE}/${id}`);
  return data;
}

export async function updateWorkOrder(
  id: string,
  payload: Partial<WorkOrderPayload>
): Promise<ApiResponse<WorkOrderResponse>> {
  const { data } = await apiClient.put<ApiResponse<WorkOrderResponse>>(
    `${BASE}/${id}`,
    toSnake(payload)
  );
  return data;
}

export async function deleteWorkOrder(
  id: string
): Promise<ApiResponse<null>> {
  const { data } = await apiClient.delete<ApiResponse<null>>(`${BASE}/${id}`);
  return data;
}

export async function startWorkOrderApi(
  id: string,
  payload: {
    work_center_id?: string;
    work_center_name?: string;
    operator_id?: string;
    operator_ids?: string[];
    operator_name?: string;
    machine_id?: string;
    machine_name?: string;
    shift_id?: string;
    shift_name?: string;
    start_time?: string;
    notes?: string;
  }
): Promise<ApiResponse<WorkOrderResponse>> {
  const { data } = await apiClient.post<ApiResponse<WorkOrderResponse>>(
    `${BASE}/${id}/start`,
    payload
  );
  return data;
}

export async function completeWorkOrderApi(
  id: string,
  payload: {
    totalQty?: number;
    completed_qty?: number;
    rejected_qty?: number;
    actual_hours?: number;
    actual_time_minutes?: number;
    notes?: string;
  }
): Promise<ApiResponse<WorkOrderResponse>> {
  const { data } = await apiClient.post<ApiResponse<WorkOrderResponse>>(
    `${BASE}/${id}/complete`,
    payload
  );
  return data;
}


// ─── Assignment and the operator's view ───────────────────────────────────────

/** Assign a planned stage: crew, machine, shift and planned time (it is started afterwards) */
export async function assignWorkOrderApi(
  id: string,
  payload: {
    operator_ids: string[];
    machine_id?: string | null;
    shift_id?: string | null;
    work_center_id?: string | null;
    planned_start_at: string;
    planned_end_at?: string;
  }
): Promise<ApiResponse<WorkOrderResponse> & { queued_materials?: number; notified?: number }> {
  const { data } = await apiClient.post(`${BASE}/${id}/assign`, payload);
  return data;
}

/** Pause a running stage: logs a downtime entry for its machine with the reason (downtime code) */
export async function pauseWorkOrderApi(
  id: string,
  payload: { downtime_code_id: string; notes?: string }
): Promise<ApiResponse<unknown>> {
  const { data } = await apiClient.post(`${BASE}/${id}/pause`, payload);
  return data;
}

/** Resume a paused stage: closes its open downtime entry */
export async function resumeWorkOrderApi(id: string): Promise<ApiResponse<unknown>> {
  const { data } = await apiClient.post(`${BASE}/${id}/resume`, {});
  return data;
}

export interface WorkOrderCostSplit {
  material: number;
  labour: number;
  /** Machine hours; part of the overhead */
  machine: number;
  /** Machine hours plus the overhead % */
  overhead: number;
  total: number;
}

export interface WorkOrderDetail extends WorkOrderResponse {
  display_status: 'PLANNED' | 'ASSIGNED' | 'LOCKED' | 'PAUSED' | string;
  po_number: string;
  po_status: string;
  item_name: string | null;
  item_code: string | null;
  unit: string;
  machine_status: string | null;
  machine_code: string | null;
  assigned_at: string | null;
  planned_start_at: string | null;
  planned_end_at: string | null;
  planned_time_minutes: number | null;
  operators_required: number;
  sequence: { current: number; total: number };
  input: { qty: number; fromWo: string | null; fromStage: string | null; fromStatus: string | null };
  instructions: string | null;
  crew: { id: string; name: string; employee_code: string | null; skill_level: string | null }[];
  materials: {
    id: string; item_name: string; item_code: string | null; unit: string | null;
    required_qty: number; issued_qty: number; status: string;
    batch_allocations: { batchNumber: string; qty: number }[] | null;
  }[];
  time: {
    plannedMinutes: number;
    startedAt: string | null;
    endedAt: string | null;
    elapsedMinutes: number | null;
    pausedMinutes: number;
    netRunMinutes: number | null;
    /** Net run minus planned, once complete */
    delayMinutes: number | null;
    /** Time booked in production entries (the actual cost basis) */
    loggedMinutes: number | null;
    pauses: { entryNumber: string; reason: string | null; category: string | null; startedAt: string; minutes: number; open: boolean }[];
  };
  /** This stage's share of the order's costing (null when it cannot be worked out) */
  cost: {
    stdHours: number;
    actHours: number;
    standard: WorkOrderCostSplit;
    actual: WorkOrderCostSplit;
    variance: number;
    plannedQty: number;
    producedQty: number;
    rejectedQty: number;
    overheadPct: number;
  } | null;
}

export async function getWorkOrderDetailApi(id: string): Promise<ApiResponse<WorkOrderDetail>> {
  const { data } = await apiClient.get(`${BASE}/${id}/detail`);
  return data;
}

/** Open work orders of the operator linked to the logged-in user */
export async function getMyWorkOrdersApi(): Promise<ApiResponse<{
  operator: { id: string; name: string; employee_code: string | null } | null;
  workOrders: WorkOrderDetail[];
}>> {
  const { data } = await apiClient.get(`${BASE}/mine`);
  return data;
}
